import type {
  Product,
  ProductBase,
  RelatedProduct,
} from "@made-people/grease-product";

const CUSTOMISATION_RELATION_TYPES = [
  "sidewing-variant",
  "lace-variant",
  "wiring-variant",
] as const;

export const isCustomizable = (relatedProducts: RelatedProduct[]): boolean => {
  return relatedProducts.some((rp) =>
    CUSTOMISATION_RELATION_TYPES.includes(rp.relationType as any),
  );
};

export const countCustomisationFeatures = (
  relatedProducts: RelatedProduct[],
): number => {
  return CUSTOMISATION_RELATION_TYPES.filter((relationType) =>
    relatedProducts.some((rp) => rp.relationType === relationType),
  ).length;
};

export const getAttributeValue = (base: ProductBase, attributeKey: string) => {
  return base.attributes.find(
    (attribute) => attribute.attributeKey === attributeKey,
  )?.value;
};

export const parseBooleanValue = (value: unknown) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "1" || normalized === "true") return true;
    if (normalized === "0" || normalized === "false") return false;
  }

  return undefined;
};

export const getAttributeBooleanValue = (
  base: ProductBase,
  attributeKey: string,
) => {
  const value = getAttributeValue(base, attributeKey);
  const parsed = parseBooleanValue(value);
  if (typeof parsed !== "undefined") {
    return parsed;
  }
  return Boolean(value);
};

export const getProductSku = (product: Product) => {
  if ("sku" in product && typeof product.sku === "string") {
    return product.sku;
  }
  return product.base.productKey;
};

export const uniqueByKey = <T>(items: T[], key: keyof T) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const value = item[key];
    const stringValue = typeof value === "string" ? value : String(value);
    if (seen.has(stringValue)) {
      return false;
    }
    seen.add(stringValue);
    return true;
  });
};
